import { Modal, Spin, Table, Upload, message } from "antd";
import {
  RightOutlined,
  UploadOutlined,
  DeleteOutlined,
  HistoryOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import TextLink from "./TextLink";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { getAllDepartmentList, getMenuList, getUserList, getUserRoleId } from "redux/selectors";

const Wrap = styled.div`
  .breadcrumb {
    display: flex;
    align-items: center;
  }
  .section-title {
    font-weight: 600;
    margin: 16px 0 8px;
  }
  .doc-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 1px solid #f0f0f0;
    border-radius: 8px;
    padding: 16px;
  }
  .doc-actions {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  .action-icon {
    cursor: pointer;
    color: #595959;
  }
`;

const formatDateTime = (value) => (value ? dayjs(value).format("Do MMM YYYY; hh:mm A") : "");

const CompanyDepartmentKpi = () => {
  const history = useHistory();
  const userList = useSelector(getUserList);
  const departmentList = useSelector(getAllDepartmentList);
  const menuList = useSelector(getMenuList);
    const userRoleId = useSelector(getUserRoleId);

    const Menulist = menuList[userRoleId] || Object.values(menuList)[0] || menuList;

  const canViewKpiCreate = Menulist?.find(
    (item) => item?.menuName === "Performance - KPI" && item?.permission?.create
  );
  const canViewDepartmentKpiCreate = Menulist?.find(
    (item) => item?.menuName === "Performance - Department KPI" && item?.permission?.create
  );

   const canViewKpidelete = Menulist?.find(
    (item) => item?.menuName === "Performance - KPI" && item?.permission?.delete
  );
  const canViewDepartmentKpidelete = Menulist?.find(
    (item) => item?.menuName === "Performance - Department KPI" && item?.permission?.delete
  );

  const [company, setCompany] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [uploadingKey, setUploadingKey] = useState(null);
  const [historyDocid, setHistoryDocid] = useState(null);
  const [historyList, setHistoryList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data, code } = await CALL_API(
        "performance-kpi-management/companykpidocs/list",
        "get"
      );
      if (code === STATUS_CODE.SUCCESS) {
        setCompany(data?.company || null);
        setDepartments(data?.departments || []);
      }
    } catch (error) {
      console.error("Error fetching company/department KPI docs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const describeAction = (actionUserid) => {
    if (!actionUserid) return "";
    const user = userList.find((u) => u.userid === actionUserid);
    const name = user?.fullname || actionUserid;
    return user?.employeeid ? `${name} (${user.employeeid})` : name;
  };

  const uploadFile = async (file, level, department) => {
    if (file.type !== "application/pdf") {
      message.error("Only PDF files are allowed");
      return;
    }
    const key = level === "company" ? "COMPANY" : department?.department_id;
    if (uploadingKey) return;
    setUploadingKey(key);
    try {
      const { key: filekey, url, code } = await CALL_API(
        "performance-kpi-management/companykpifile",
        "post",
        { filename: file.name, type: file.type }
      );
      if (code !== STATUS_CODE.SUCCESS || !url) {
        message.error("Failed to get upload URL");
        return;
      }
      const putRes = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!putRes.ok) {
        message.error("File upload failed");
        return;
      }
      const { code: saveCode } = await CALL_API(
        "performance-kpi-management/companykpidocs",
        "post",
        {
          level,
          departmentid: department?.department_id,
          departmentname: department?.department_name,
          filename: file.name,
          filekey,
        }
      );
      if (saveCode === STATUS_CODE.SUCCESS) {
        message.success("Document uploaded successfully");
        fetchData();
      }
    } catch (error) {
      console.error("Error uploading KPI document:", error);
      message.error("Something went wrong");
    } finally {
      setUploadingKey(null);
    }
  };

  const onDelete = (docid) => {
    Modal.confirm({
      title: "Delete this document?",
      icon: null,
      centered: true,
      okText: "Yes, Delete",
      cancelText: "Cancel",
      okButtonProps: { danger: true },
      onOk: async () => {
        const { code } = await CALL_API(
          `performance-kpi-management/companykpidocs/${docid}`,
          "delete"
        );
        if (code === STATUS_CODE.SUCCESS) {
          message.success("Document deleted");
          fetchData();
        }
      },
    });
  };

  const onView = async (docid, filename) => {
    try {
      const { url, code } = await CALL_API(
        `performance-kpi-management/companykpifile/${docid}`,
        "get"
      );
      if (code === STATUS_CODE.SUCCESS) {
        setPreviewUrl(url);
        setPreviewTitle(filename);
      }
    } catch (error) {
      console.error("Error fetching KPI document preview:", error);
    }
  };

  const openHistory = async (docid) => {
    setHistoryDocid(docid);
    setHistoryLoading(true);
    try {
      const { historyList: list, code } = await CALL_API(
        `performance-kpi-management/companykpidocshistory/${docid}`,
        "get"
      );
      if (code === STATUS_CODE.SUCCESS) setHistoryList(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Error fetching KPI document history:", error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const historyColumns = [
    {
      title: "Action by",
      key: "actionby",
      render: (_, record) => (
        <>
          {record.action === "uploaded" ? "Created" : "Deleted"} by <b>{describeAction(record.actionby)}</b>
        </>
      ),
    },
    { title: "Date & Time", dataIndex: "actionat", key: "actionat", render: formatDateTime },
  ];

  const renderActionBy = (row) => {
    if (row?.filename) {
      return `Uploaded by: ${describeAction(row.uploadedby)}; ${formatDateTime(row.uploadedon)}`;
    }
    if (row?.deletedby) {
      return `Deleted by: ${describeAction(row.deletedby)}; ${formatDateTime(row.deletedon)}`;
    }
    return "---";
  };

  const departmentRows = departmentList.map((dept) => {
    const row = departments.find((d) => d.departmentid === dept.department_id);
    return {
      departmentid: dept.department_id,
      departmentname: dept.department_name,
      ...row,
    };
  });

  const columns = [
    { title: "Department", dataIndex: "departmentname", key: "departmentname" },
    {
      title: "File",
      key: "file",
      render: (_, row) =>
        row.filename ? (
          <TextLink onClick={() => onView(row.departmentid, row.filename)}>
            {row.filename}
          </TextLink>
        ) : (
          "No Document available"
        ),
    },
    {
      title: "Action by",
      key: "actionby",
      render: (_, row) => renderActionBy(row),
    },
    {
      key: "actions",
      width: 120,
      render: (_, row) => (
        <div className="doc-actions">
          <HistoryOutlined className="action-icon" onClick={() => openHistory(row.departmentid)} />
            {canViewDepartmentKpiCreate &&(

          <Upload
            showUploadList={false}
            disabled={Boolean(uploadingKey)}
            beforeUpload={(file) => {
              uploadFile(file, "department", {
                department_id: row.departmentid,
                department_name: row.departmentname,
              });
              return false;
            }}
          >
            {uploadingKey === row.departmentid ? (
              <LoadingOutlined className="action-icon" />
            ) : (
              <UploadOutlined className="action-icon" />
            )}
          </Upload>
            )}
          {row.filename && canViewDepartmentKpidelete && (
            <DeleteOutlined
              className="action-icon"
              onClick={() => onDelete(row.departmentid)}
            />
          )}
        </div>
      ),
    },
  ];
    

  return (
    <Wrap>
      <Spin spinning={loading}>
        <div className="breadcrumb mb-3">
          <p
            className="lite-text p-0 m-0"
            style={{ cursor: "pointer" }}
            onClick={() => history.push("/performance")}
          >
            Performance Management
          </p>
          &nbsp;
          <RightOutlined className="mt-1" />
          &nbsp;
          <p className="m-0 p-0">Company & Departmental KPIs</p>
        </div>

        <div className="section-title">Company Level KPI</div>
        <div className="doc-row">
          <div>
            {company?.filename ? (
              <TextLink onClick={() => onView("COMPANY", company.filename)}>
                {company.filename}
              </TextLink>
            ) : (
              <span>No Document available</span>
            )}
            <div className="lite-text">{renderActionBy(company)}</div>
          </div>
          <div className="doc-actions">
            <HistoryOutlined className="action-icon" onClick={() => openHistory("COMPANY")} />
              {canViewKpiCreate &&(

            <Upload
              showUploadList={false}
              disabled={Boolean(uploadingKey)}
              beforeUpload={(file) => {
                uploadFile(file, "company");
                return false;
              }}
            >
              {uploadingKey === "COMPANY" ? (
                <LoadingOutlined className="action-icon" />
              ) : (
                <UploadOutlined className="action-icon" />
              )}
            </Upload>
              )}
            {company?.filename && canViewKpidelete && (
              <DeleteOutlined className="action-icon" onClick={() => onDelete("COMPANY")} />
            )}
          </div>
        </div>

        <div className="section-title mt-4">Departmental Level KPI</div>
        <Table
          columns={columns}
          dataSource={departmentRows}
          pagination={false}
          rowKey={(row) => row.departmentid}
        />
      </Spin>

      <Modal
        title={previewTitle}
        open={Boolean(previewUrl)}
        onCancel={() => setPreviewUrl("")}
        footer={null}
        width={900}
        centered
      >
        {previewUrl && (
          <iframe
            title={previewTitle}
            src={previewUrl}
            width="100%"
            height="500"
            style={{ border: "none" }}
          />
        )}
      </Modal>

      <Modal
        title="History"
        open={Boolean(historyDocid)}
        onCancel={() => setHistoryDocid(null)}
        footer={null}
        width={640}
        centered
      >
        <Table
          columns={historyColumns}
          dataSource={historyList}
          loading={historyLoading}
          pagination={false}
          rowKey={(record) => record.id}
        />
      </Modal>
    </Wrap>
  );
};

export default CompanyDepartmentKpi;
